import { Obj, load } from "scrivito"
import {
  ProductAttributes,
  ProductObj
} from "scrivito/Objs/Product/ProductObjClass"
import {
  ProductAddonAttributes,
  ProductAddonObj
} from "scrivito/Objs/ProductAddon/ProductAddonObjClass"
import { ProductBaseVariantObj } from "scrivito/Objs/ProductBaseVariant/ProductBaseVariantObjClass"

import { getVariantKeyAttributes } from "entities/products/lib/getProductType"

import { getVariantKeyPrefixLength } from "../variant-key/getVariantKeyPrefixLength"

export function loadProductAddons(
  productObj: ProductObj
): Promise<(Obj<ProductAttributes | ProductAddonAttributes> | undefined)[]> {
  return load(() => productObj?.get("addons")).then(addonObjs =>
    Promise.all(
      addonObjs?.map(addon =>
        load(
          () =>
            addon.get("addon") as
              | Obj<ProductAttributes | ProductAddonAttributes>
              | undefined
        )
      ) || []
    )
  )
}

export interface GetProductAddonObjArgs {
  productObj: ProductObj | null | undefined
  configurationID: string
}

export function getProductAddonObj({
  productObj,
  configurationID
}: GetProductAddonObjArgs):
  | Obj<ProductAttributes | ProductAddonAttributes>
  | undefined {
  return productObj
    ?.get("addons")
    .find(addonObj => addonObj.get("configurationID") === configurationID)
    ?.get("addon") as
    | Obj<ProductAttributes | ProductAddonAttributes>
    | undefined
}

export function getProductAddonBaseVariants(
  args: GetProductAddonObjArgs
): ProductBaseVariantObj[] | null | undefined {
  const addon = getProductAddonObj(args)

  let baseVariants: ProductBaseVariantObj[] | null | undefined = []
  if (addon?.objClass() === "Product") {
    baseVariants = (addon as ProductObj).children() as ProductBaseVariantObj[]
  } else if (addon?.objClass() === "ProductAddon") {
    baseVariants = (addon as ProductAddonObj).get(
      "baseVariants"
    ) as ProductBaseVariantObj[]
  }
  return baseVariants
}

export interface GetMatchingProductAddonBaseVariantsArgs
  extends GetProductAddonObjArgs {
  addonVariantKey: string
}

export function getMatchingProductAddonBaseVariants({
  productObj,
  configurationID,
  addonVariantKey
}: GetMatchingProductAddonBaseVariantsArgs):
  | ProductBaseVariantObj[]
  | null
  | undefined {
  const baseVariants = getProductAddonBaseVariants({
    productObj,
    configurationID
  })
  const attributes = getVariantKeyAttributes(addonVariantKey)

  return (
    baseVariants
      ?.map(child => ({
        child: child as ProductBaseVariantObj,
        prefixLength: getVariantKeyPrefixLength(
          getVariantKeyAttributes(child.get("variantKey") as string),
          attributes,
          true
        )
      }))
      .filter(child => child.prefixLength !== -1)
      .sort((a, b) => (a.prefixLength > b.prefixLength ? -1 : 1))
      .map(p => p.child) || null
  )
}

export function getHighestMatchingProductAddonBaseVariant(
  args: GetMatchingProductAddonBaseVariantsArgs
): ProductBaseVariantObj | null {
  return getMatchingProductAddonBaseVariants(args)?.[0] || null
}
